import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type * as type from "@vTypes";
import type { CopyNurses } from "@/utils/rosterCreate-util";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";

export function useEmergencyReplacement({
  setCopyNurses,
  defaultShiftCode,
  addModifiedCell,
}: {
  setCopyNurses: Dispatch<SetStateAction<CopyNurses>>;
  defaultShiftCode: type.availableShifts[];
  /** 수동 수정과 동일하게 펜 아이콘을 남길 때 사용 */
  addModifiedCell: (nurseId: string, dayIndex: number) => void;
}) {
  const setUiMode = useRosterCreateUiStore((s) => s.setMode);

  const [emergencySlotPick, setEmergencySlotPick] =
    useState<EmergencyReplacementSlotPick | null>(null);
  const [emergencyBulkPeriodPick, setEmergencyBulkPeriodPick] =
    useState<EmergencyBulkPeriodPick | null>(null);
  /** 모달이 열린 채로 당일/기간 라디오를 바꿀 때 동기화 (ButtonGroup에서 수신) */
  const [emergencyReplacementPeriodMode, setEmergencyReplacementPeriodMode] =
    useState<"sameDay" | "period">("sameDay");
  /** BULK: 모달에서 시나리오 선택 시 steps 동기화 → TableBody 하이라이트 */
  const [emergencyBulkSelectedSteps, setEmergencyBulkSelectedSteps] =
    useState<Array<{ date: string; nurseId: string }> | null>(null);
  /** 모달에서 SINGLE 추천 결과를 받은 상태 — true면 뒤 테이블 셀 재클릭 시 confirm */
  const [emergencySingleHasResult, setEmergencySingleHasResult] =
    useState(false);
  /** 모달에서 BULK 추천 결과를 받은 상태 — true면 뒤 테이블 기간 셀 재클릭 시 confirm */
  const [emergencyBulkHasResult, setEmergencyBulkHasResult] =
    useState(false);
  /** 뒤 테이블에서 confirm 승인 시 모달 결과를 초기화하는 콜백 (모달에서 등록) */
  const bulkResultResetRef = useRef<(() => void) | null>(null);
  const [
    emergencyReplacementCandidateNurseId,
    setEmergencyReplacementCandidateNurseId,
  ] = useState<string | null>(null);
  const [emergencyPostApplyHighlight, setEmergencyPostApplyHighlight] =
    useState<EmergencyPostApplyHighlight | null>(null);
  const emergencySlotClearTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const cancelEmergencySlotPickClear = useCallback(() => {
    if (emergencySlotClearTimeoutRef.current != null) {
      clearTimeout(emergencySlotClearTimeoutRef.current);
      emergencySlotClearTimeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      cancelEmergencySlotPickClear();
    },
    [cancelEmergencySlotPickClear]
  );

  const resetEmergencyOnDateChange = useCallback(() => {
    setEmergencyPostApplyHighlight(null);
    if (emergencySlotClearTimeoutRef.current != null) {
      clearTimeout(emergencySlotClearTimeoutRef.current);
      emergencySlotClearTimeoutRef.current = null;
    }
    setEmergencySlotPick(null);
    setEmergencyBulkPeriodPick(null);
    setEmergencySingleHasResult(false);
    setEmergencyReplacementCandidateNurseId(null);
  }, []);

  const handleEmergencyModalOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setUiMode("none");
        setEmergencyReplacementCandidateNurseId(null);
        setEmergencySlotPick(null);
        setEmergencyBulkPeriodPick(null);
        setEmergencyReplacementPeriodMode("sameDay");
        setEmergencyBulkSelectedSteps(null);
        setEmergencySingleHasResult(false);
        setEmergencyBulkHasResult(false);
      } else {
        setUiMode("emergency");
        cancelEmergencySlotPickClear();
        setEmergencyPostApplyHighlight(null);
      }
    },
    [cancelEmergencySlotPickClear, setUiMode]
  );

  const openEmergencyModalFromButton = useCallback(() => {
    cancelEmergencySlotPickClear();
    setEmergencySlotPick(null);
    setEmergencyBulkPeriodPick(null);
    setEmergencySingleHasResult(false);
    setEmergencyReplacementPeriodMode("sameDay");
    setEmergencyPostApplyHighlight(null);
    setUiMode("emergency");
  }, [cancelEmergencySlotPickClear, setUiMode]);

  /**
   * 기간 지정 모드 셀 클릭 핸들러 — 상태 머신: idle → startPicked → rangeComplete → startPicked …
   * hasExistingResult: 검색 결과가 이미 있을 때 true (확인 대화상자 필요)
   */
  const handleBulkPeriodCellPick = useCallback(
    (
      pick: { nurseId: string; nurseName: string; dayIndex: number; dateISO: string; shiftColor?: string },
      hasExistingResult: boolean
    ) => {
      const current = emergencyBulkPeriodPick;

      // rangeComplete 상태에서 추가 클릭 → 기존 범위 리셋 후 새 startPicked
      if (current?.endDateISO != null) {
        if (hasExistingResult) {
          const ok = window.confirm(
            "기간 지정 추천 결과가 있습니다. 기간을 다시 선택하면 기존 결과가 초기화됩니다.\n계속하시겠습니까?"
          );
          if (!ok) return;
          // 모달 추천 결과 초기화 (뮤테이션 리셋 + hasBulkResult false)
          bulkResultResetRef.current?.();
          setEmergencyBulkHasResult(false);
        }
        setEmergencyBulkPeriodPick({
          nurseId: pick.nurseId,
          nurseName: pick.nurseName,
          startDayIndex: pick.dayIndex,
          startDateISO: pick.dateISO,
          shiftColor: pick.shiftColor,
        });
        return;
      }

      // idle 상태 또는 다른 간호사 → startPicked (결과 있으면 함께 초기화)
      if (current == null || current.nurseId !== pick.nurseId) {
        if (hasExistingResult) {
          bulkResultResetRef.current?.();
          setEmergencyBulkHasResult(false);
        }
        setEmergencyBulkPeriodPick({
          nurseId: pick.nurseId,
          nurseName: pick.nurseName,
          startDayIndex: pick.dayIndex,
          startDateISO: pick.dateISO,
          shiftColor: pick.shiftColor,
        });
        return;
      }

      // 같은 간호사 2회 클릭 → rangeComplete (자동 스왑 보장)
      const [startIdx, endIdx] =
        pick.dayIndex >= current.startDayIndex
          ? [current.startDayIndex, pick.dayIndex]
          : [pick.dayIndex, current.startDayIndex];
      const [startDate, endDate] =
        pick.dayIndex >= current.startDayIndex
          ? [current.startDateISO, pick.dateISO]
          : [pick.dateISO, current.startDateISO];

      setEmergencyBulkPeriodPick({
        ...current,
        startDayIndex: startIdx,
        startDateISO: startDate,
        endDayIndex: endIdx,
        endDateISO: endDate,
      });
    },
    [emergencyBulkPeriodPick]
  );

  const handleEmergencyReplacementCellPick = useCallback(
    (
      pick: EmergencyReplacementSlotPick,
      hasExistingBulkResult: boolean,
      hasExistingSingleResult: boolean
    ) => {
      const current = emergencyBulkPeriodPick;

      const resetBulkResultIfNeeded = () => {
        if (!hasExistingBulkResult) return true;
        const ok = window.confirm(
          "기간 지정 추천 결과가 있습니다. 대상을 다시 선택하면 기존 결과가 초기화됩니다.\n계속하시겠습니까?"
        );
        if (!ok) return false;
        bulkResultResetRef.current?.();
        setEmergencyBulkHasResult(false);
        return true;
      };

      const resetSingleResultIfNeeded = () => {
        if (!hasExistingSingleResult) return true;
        const ok = window.confirm(
          "이미 검색된 결과가 있습니다. 대상을 변경하면 기존 추천 결과가 초기화됩니다.\n대상을 변경하시겠습니까?"
        );
        if (!ok) return false;
        setEmergencySingleHasResult(false);
        return true;
      };

      const setSinglePick = () => {
        if (!resetBulkResultIfNeeded()) return;
        if (!resetSingleResultIfNeeded()) return;
        setEmergencySlotPick(pick);
        setEmergencyBulkPeriodPick({
          nurseId: pick.nurseId,
          nurseName: pick.nurseName,
          startDayIndex: pick.dayIndex,
          startDateISO: pick.dateISO,
          shiftColor: pick.shiftColor,
        });
        setEmergencyReplacementPeriodMode("sameDay");
        setEmergencyBulkSelectedSteps(null);
        setEmergencyReplacementCandidateNurseId(null);
      };

      if (
        current == null ||
        current.endDateISO != null ||
        String(current.nurseId) !== String(pick.nurseId)
      ) {
        setSinglePick();
        return;
      }

      if (current.startDayIndex === pick.dayIndex) {
        setSinglePick();
        return;
      }

      const [startIdx, endIdx] =
        pick.dayIndex >= current.startDayIndex
          ? [current.startDayIndex, pick.dayIndex]
          : [pick.dayIndex, current.startDayIndex];
      const [startDate, endDate] =
        pick.dayIndex >= current.startDayIndex
          ? [current.startDateISO, pick.dateISO]
          : [pick.dateISO, current.startDateISO];

      setEmergencyBulkPeriodPick({
        ...current,
        startDayIndex: startIdx,
        startDateISO: startDate,
        endDayIndex: endIdx,
        endDateISO: endDate,
        shiftColor: current.shiftColor ?? pick.shiftColor,
      });
      setEmergencySlotPick(null);
      setEmergencyReplacementPeriodMode("period");
      setEmergencyBulkSelectedSteps(null);
      setEmergencyReplacementCandidateNurseId(null);
    },
    [emergencyBulkPeriodPick]
  );

  const applyEmergencyReplacement = useCallback(
    ({
      nurseId,
      targetNurseId,
      dayIndex,
      shiftCode,
    }: {
      nurseId: string;
      targetNurseId: string;
      dayIndex: number;
      shiftCode: string;
    }) => {
      const offShift =
        [...defaultShiftCode]
          .filter((s) => s.default_shift === "O")
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0] ?? null;
      const offShiftCode = offShift?.shift_id ?? "O";
      const offShiftDbId =
        offShift?.id != null && Number.isFinite(Number(offShift.id))
          ? Number(offShift.id)
          : null;
      const replacementShift =
        defaultShiftCode.find((s) => s.shift_id === shiftCode) ??
        defaultShiftCode.find((s) => s.default_shift === shiftCode) ??
        null;
      const replacementShiftDbId =
        replacementShift?.id != null &&
        Number.isFinite(Number(replacementShift.id))
          ? Number(replacementShift.id)
          : null;
      setCopyNurses((prev) =>
        prev.map((nurse) => {
          const currentNurseId = String(nurse.nurse_id);
          const replacementNurseId = String(nurseId);
          const originalTargetNurseId = String(targetNurseId);
          const isReplacementNurse = currentNurseId === replacementNurseId;
          const isOriginalTargetNurse =
            currentNurseId === originalTargetNurseId;
          if (!isReplacementNurse && !isOriginalTargetNurse) return nurse;
          const nextSchedule = Array.isArray(nurse.schedule)
            ? [...nurse.schedule]
            : [];
          const rawIds = (nurse as { schedule_ids?: (number | null)[] | null })
            .schedule_ids;
          const len = Math.max(nextSchedule.length, rawIds?.length ?? 0);
          const nextIds: (number | null)[] = Array.from(
            { length: len },
            (_, i) => rawIds?.[i] ?? null
          );
          if (dayIndex < 0 || dayIndex >= nextSchedule.length) return nurse;
          if (isReplacementNurse) {
            nextSchedule[dayIndex] = shiftCode;
            if (dayIndex < len) {
              nextIds[dayIndex] = replacementShiftDbId;
            }
          } else if (isOriginalTargetNurse) {
            nextSchedule[dayIndex] = offShiftCode;
            if (dayIndex < len) {
              nextIds[dayIndex] = offShiftDbId;
            }
          }
          return { ...nurse, schedule: nextSchedule, schedule_ids: nextIds };
        })
      );
      // 수동 수정과 동일하게 펜 아이콘 등록 (저장 전까지 유지)
      addModifiedCell(String(nurseId), dayIndex);
      addModifiedCell(String(targetNurseId), dayIndex);
      setEmergencyPostApplyHighlight({
        targetNurseId: String(targetNurseId),
        replacementNurseId: String(nurseId),
        dayIndex,
      });
      cancelEmergencySlotPickClear();
      emergencySlotClearTimeoutRef.current = setTimeout(() => {
        setEmergencySlotPick(null);
        setEmergencyPostApplyHighlight(null);
        emergencySlotClearTimeoutRef.current = null;
      }, 10000);
    },
    [
      setCopyNurses,
      defaultShiftCode,
      cancelEmergencySlotPickClear,
      addModifiedCell,
    ]
  );

  return {
    emergencySlotPick,
    setEmergencySlotPick,
    emergencySingleHasResult,
    setEmergencySingleHasResult,
    emergencyBulkPeriodPick,
    setEmergencyBulkPeriodPick,
    emergencyBulkSelectedSteps,
    setEmergencyBulkSelectedSteps,
    emergencyBulkHasResult,
    setEmergencyBulkHasResult,
    bulkResultResetRef,
    emergencyReplacementPeriodMode,
    setEmergencyReplacementPeriodMode,
    handleBulkPeriodCellPick,
    handleEmergencyReplacementCellPick,
    emergencyReplacementCandidateNurseId,
    setEmergencyReplacementCandidateNurseId,
    emergencyPostApplyHighlight,
    setEmergencyPostApplyHighlight,
    cancelEmergencySlotPickClear,
    handleEmergencyModalOpenChange,
    openEmergencyModalFromButton,
    applyEmergencyReplacement,
    resetEmergencyOnDateChange,
  };
}
